// A Previsão de desembolso no gráfico da Projeção Financeira (revisão de 2026-10-07): um ícone
// de informação no canto inferior direito do gráfico. Ao passar o mouse (ou focar pelo
// teclado) abre o detalhamento — o estado da curva, a cobertura (a MESMA da área de trabalho,
// `ProjectedCoverage`) e o atalho para o módulo. Substitui o chip da faixa acima do gráfico:
// o desembolso agora tem regra fixa, então não há fonte nem política a escolher ali.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { normalizeDisbursementCoverage, projectedCoverage } from "./coverage";
import ProjectedCoverage, { fractionLabel } from "./ProjectedCoverage";
import {
  ampliCurveState,
  ampliCurveStateLabel,
  ampliInconsistentReasonLabel,
  isAmpliDisbursementPolicy,
} from "../financialProjection/cashFlowPlanning";

const NEEDS_ATTENTION = new Set(["stale", "inconsistent", "unavailable"]);

// Cópia do texto de `DisbursementHeader#STATE_HINT` só pro caso `unavailable` sem
// motivo — importar do outro componente criaria um ciclo entre os dois módulos.
const PLAIN_UNAVAILABLE_HINT =
  "Esta obra ainda não tem previsto canônico para gerar a curva.";

// Fechar com um pequeno atraso deixa o mouse atravessar do ícone para o detalhe.
const CLOSE_DELAY_MS = 150;

const AmpliDisbursementCoverageCard = ({ project }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const coverage = normalizeDisbursementCoverage(project?.ampliDisbursement?.coverage);
  const projected = projectedCoverage(coverage);

  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);
  // O detalhe não sobrevive a uma troca de obra por baixo do mesmo componente montado.
  useEffect(() => {
    setOpen(false);
  }, [project?._id]);

  if (!isAmpliDisbursementPolicy(project?.cashFlowPlanning)) return null;

  const curve = ampliCurveState(project);
  const needsAttention = NEEDS_ATTENTION.has(curve.status);
  const stateDetails = {
    stale: "A curva anterior continua no ar enquanto a nova é gerada.",
    unavailable: PLAIN_UNAVAILABLE_HINT,
  };
  const detail = curve.reason
    ? ampliInconsistentReasonLabel(curve.reason)
    : stateDetails[curve.status] ?? null;
  const summary = needsAttention
    ? ampliCurveStateLabel(curve.status)
    : `${fractionLabel(projected?.percentage)} coberto`;

  return (
    <div
      className="chart-info ampli-coverage-info"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) hide();
      }}
    >
      <button
        type="button"
        className={`chart-info__trigger${needsAttention ? " chart-info__trigger--attention" : ""}`}
        aria-label={`Previsão de desembolso: ${summary}. Ver detalhamento`}
        aria-expanded={open}
        aria-controls="ampli-coverage-info-detail"
        data-testid="coverage-chip-covered"
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">i</span>
      </button>

      {open ? (
        <section
          id="ampli-coverage-info-detail"
          className={`chart-info__detail ${curve.status}`}
          role="dialog"
          aria-label="Detalhamento da previsão de desembolso"
        >
          <span className="coverage-eyebrow">Fluxo de caixa · previsão de desembolso</span>
          <h3>{ampliCurveStateLabel(curve.status)}</h3>
          {detail ? <p>{detail}</p> : null}
          {coverage ? <ProjectedCoverage coverage={coverage} /> : null}
          <Link className="button button-accent small" to={`/dashboard/adiantamentos/${project._id}`}>
            Abrir o módulo Desembolso
          </Link>
        </section>
      ) : null}
    </div>
  );
};

export default AmpliDisbursementCoverageCard;
